// Tenantra Inc. - Back office: batched platform-fee payouts.
// Fees accrue in each landlord's wallet; here an admin sweeps them to Tenantra's bank as one
// consolidated EFT per landlord (or all owed at once), by month, plus an optional monthly auto-run.
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { apiHelpers } from '@/services/api';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import { DetailSheet, Section, fmtDate } from '@/components/dashboard/DetailPanel';

const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const months = () => Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
  return { value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }) };
});

const STATUS = {
  processing: { label: 'Processing', cls: 'bg-amber-100 text-amber-800' },
  scheduled: { label: 'Scheduled', cls: 'bg-slate-100 text-slate-700' },
  settled: { label: 'Paid', cls: 'bg-teal-100 text-teal-800' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
};
const Chip = ({ status }) => {
  const s = STATUS[status] || { label: status || '—', cls: 'bg-slate-100 text-slate-600' };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`}>{s.label}</span>;
};

function Tiles({ totals }) {
  const tiles = [
    { id: 'owed', label: 'Owed to Tenantra', value: money(totals.owed), note: totals.owed ? 'Accrued, not yet swept' : 'Nothing outstanding', ok: totals.owed > 0 },
    { id: 'landlords', label: 'Landlords owing', value: totals.landlords_owing, note: 'Each = one EFT when run' },
    { id: 'payments', label: 'Payments', value: totals.payments, note: 'Settled, fee-bearing' },
    { id: 'paid', label: 'Paid out this month', value: money(totals.paid), note: totals.paid ? 'Already swept to your bank' : 'None swept yet', ok: totals.paid > 0 },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-5" data-testid="fee-payout-stats">
      {tiles.map(t => (
        <div key={t.id} className="pr-4 lg:border-r lg:last:border-0 border-slate-200 lg:pl-4 lg:first:pl-0" data-testid={`fee-payout-stat-${t.id}`}>
          <p className="text-slate-500 text-xs mb-0.5">{t.label}</p>
          <p className="text-2xl font-bold tabular-nums text-slate-900" data-testid={`fee-payout-stat-${t.id}-value`}>{t.value}</p>
          <p className={`text-xs mt-1 ${t.ok ? 'text-teal-700' : 'text-slate-400'}`}>{t.note}</p>
        </div>
      ))}
    </div>
  );
}

function AutoConfig({ config, onSave }) {
  const [auto, setAuto] = useState(config.auto);
  const [day, setDay] = useState(String(config.day || 1));
  useEffect(() => { setAuto(config.auto); setDay(String(config.day || 1)); }, [config]);
  const save = (nextAuto, nextDay) => onSave({ auto: nextAuto, day: Number(nextDay) });
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="fee-payout-config">
      <div>
        <p className="text-sm font-medium text-slate-800">Automatic monthly payout</p>
        <p className="text-[11px] text-slate-500">{auto ? `Runs automatically on day ${day} each month for the previous month's fees.` : 'Off — you run payouts manually.'}</p>
      </div>
      <div className="flex items-center gap-3">
        <Select value={day} onValueChange={(v) => { setDay(v); if (auto) save(auto, v); }}>
          <SelectTrigger className="h-9 w-28 text-sm bg-white" data-testid="fee-payout-day"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-64">{Array.from({ length: 28 }, (_, i) => i + 1).map(d => <SelectItem key={d} value={String(d)}>Day {d}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant={auto ? 'default' : 'outline'} className={auto ? 'bg-teal-600 hover:bg-teal-700 h-9' : 'h-9'} onClick={() => { const n = !auto; setAuto(n); save(n, day); }} data-testid="fee-payout-auto-toggle">
          {auto ? 'On' : 'Off'}
        </Button>
      </div>
    </div>
  );
}

function LandlordSheet({ row, month, running, onRun, onClose }) {
  return (
    <DetailSheet open={!!row} onClose={onClose} crumb="Fee payout" title={row?.landlord_name} testId="fee-payout-landlord-sheet"
      meta={row && <span className="text-slate-500">{row.count} payments · {money(row.owed)} owed</span>}>
      <Section title="By property">
        <ul className="divide-y divide-slate-100 -mx-1">
          {(row?.properties || []).map((p, i) => (
            <li key={i} className="px-1 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`fee-payout-prop-${i}`}>
              <span className="text-slate-700 truncate">{p.address}</span>
              <span className="text-right tabular-nums shrink-0"><span className="text-slate-500 text-xs mr-2">{p.count}</span>{money(p.owed)}</span>
            </li>
          ))}
        </ul>
      </Section>
      {row?.owed > 0 && (
        <Button disabled={running} onClick={() => onRun(row.landlord_id)} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="fee-payout-run-landlord">
          {running ? 'Starting…' : `Pay out ${money(row.owed)} to Tenantra`}
        </Button>
      )}
    </DetailSheet>
  );
}

function BatchSheet({ batchId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!batchId) { setData(null); return; }
    setData(null);
    api.get(`/admin/fee-payouts/batches/${batchId}`).then(r => setData(r.data)).catch(e => { toast.error(apiHelpers.handleError(e)); onClose(); });
  }, [batchId]);
  return (
    <DetailSheet open={!!batchId} onClose={onClose} loading={!data} crumb="Batch" title={data?.landlord_name} testId="fee-payout-batch-sheet"
      meta={data && <span className="text-slate-500">{data.count} payments · {money(data.amount)} · <Chip status={data.status} /></span>}>
      {data && (
        <Section title="Zum transfer">
          <p className="text-[11px] text-slate-500 break-all">Transaction: {data.zum_transaction_id || '—'}</p>
          {data.error && <p className="text-xs text-red-600 mt-1">{data.error}</p>}
        </Section>
      )}
      <Section title="Payments in this batch">
        <ul className="divide-y divide-slate-100 -mx-1">
          {(data?.payments || []).map(p => (
            <li key={p.id} className="px-1 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`fee-payout-batch-pay-${p.id}`}>
              <div className="min-w-0">
                <p className="text-slate-800 truncate">{p.reference || 'No reference'}</p>
                <p className="text-[11px] text-slate-400">{fmtDate(p.created_at)} · rent {money(p.amount)}</p>
              </div>
              <span className="text-right tabular-nums text-teal-700 shrink-0">{money(p.fee)}</span>
            </li>
          ))}
        </ul>
      </Section>
    </DetailSheet>
  );
}

export function FeePayoutsPanel() {
  const options = useMemo(months, []);
  const [month, setMonth] = useState(options[0].value);
  const [data, setData] = useState(null);
  const [batches, setBatches] = useState(null);
  const [running, setRunning] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [openBatch, setOpenBatch] = useState(null);
  const label = options.find(m => m.value === month)?.label;

  const load = () => {
    api.get('/admin/fee-payouts/summary', { params: { month } }).then(r => setData(r.data)).catch(e => toast.error(apiHelpers.handleError(e)));
    api.get('/admin/fee-payouts/batches', { params: { month } }).then(r => setBatches(r.data.items)).catch(() => setBatches([]));
  };
  useEffect(() => { setData(null); setBatches(null); load(); /* eslint-disable-next-line */ }, [month]);

  const saveConfig = (cfg) => api.put('/admin/fee-payouts/config', cfg)
    .then(r => { setData(d => ({ ...d, config: r.data.config })); toast.success('Automatic payout updated'); })
    .catch(e => toast.error(apiHelpers.handleError(e)));

  const runPayout = async (landlordId) => {
    const target = landlordId ? data.rows.find(r => r.landlord_id === landlordId) : null;
    const amount = landlordId ? target?.owed : data.totals.owed;
    const who = landlordId ? target?.landlord_name : `${data.totals.landlords_owing} landlord(s)`;
    if (!window.confirm(`Pay out ${money(amount)} in fees from ${who} to Tenantra's bank now?`)) return;
    setRunning(true);
    try {
      await api.post('/admin/fee-payouts/run', { month, landlord_id: landlordId || undefined });
      toast.success('Payout run started — transfers are being created.');
      setOpenRow(null);
      setTimeout(load, 4000);
      setTimeout(load, 12000);
    } catch (e) {
      toast.error(apiHelpers.handleError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fee-payouts-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Fee Payouts</h1>
          <p className="text-sm text-slate-500">Sweep accrued platform fees to Tenantra's bank — one EFT per landlord. {label}.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button disabled={!data || running || !data.totals.owed} onClick={() => runPayout(null)} className="h-9 bg-teal-600 hover:bg-teal-700" data-testid="fee-payout-run-all">
            {running ? 'Starting…' : data?.totals?.owed ? `Run all owed · ${money(data.totals.owed)}` : 'Nothing owed'}
          </Button>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-9 w-48 text-sm bg-white" data-testid="fee-payout-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{options.map(m => <SelectItem key={m.value} value={m.value} data-testid={`fee-payout-month-${m.value}`}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!data ? <TableSkeleton rows={5} spans={[5, 2, 2, 2, 1]} /> : (
        <>
          <Tiles totals={data.totals} />
          <AutoConfig config={data.config || { auto: false, day: 1 }} onSave={saveConfig} />

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="fee-payout-table">
            <div className="grid grid-cols-12 gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50 text-[11px] font-medium text-slate-500 uppercase tracking-wide">
              <div className="col-span-5">Landlord</div>
              <div className="col-span-2 text-right">Payments</div>
              <div className="col-span-2 text-right">Owed</div>
              <div className="col-span-3 text-right">Action</div>
            </div>
            <div className="divide-y divide-slate-100">
              {data.rows.length === 0 && <p className="px-4 py-8 text-sm text-slate-500 text-center" data-testid="fee-payout-empty">No fees owed for {label}. Everything's swept.</p>}
              {data.rows.map(r => (
                <div key={r.landlord_id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-slate-50" data-testid={`fee-payout-row-${r.landlord_id}`}>
                  <button onClick={() => setOpenRow(r)} className="col-span-5 min-w-0 text-left" data-testid={`fee-payout-open-${r.landlord_id}`}>
                    <p className="text-sm font-medium text-slate-800 truncate">{r.landlord_name}</p>
                    <p className="text-[11px] text-slate-400">{r.properties.length} propert{r.properties.length === 1 ? 'y' : 'ies'}{r.paid > 0 ? ` · ${money(r.paid)} paid` : ''}</p>
                  </button>
                  <div className="col-span-2 text-right text-sm tabular-nums text-slate-700">{r.count}</div>
                  <div className="col-span-2 text-right text-sm tabular-nums font-medium text-teal-700">{money(r.owed)}</div>
                  <div className="col-span-3 flex justify-end">
                    <Button size="sm" variant="outline" disabled={running || r.owed <= 0} onClick={() => runPayout(r.landlord_id)} className="h-8 text-xs" data-testid={`fee-payout-run-${r.landlord_id}`}>Pay out</Button>
                  </div>
                </div>
              ))}
              {data.rows.length > 0 && (
                <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center bg-slate-50 font-semibold text-sm" data-testid="fee-payout-totals">
                  <div className="col-span-5 text-slate-900">Total owed</div>
                  <div className="col-span-2 text-right tabular-nums">{data.totals.payments}</div>
                  <div className="col-span-2 text-right tabular-nums text-teal-700">{money(data.totals.owed)}</div>
                  <div className="col-span-3" />
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="fee-payout-history">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-900">Payout history · {label}</p>
              <p className="text-[11px] text-slate-500">Each row is one EFT to Tenantra's bank.</p>
            </div>
            <div className="divide-y divide-slate-100">
              {batches === null && <p className="px-4 py-6 text-sm text-slate-400">Loading…</p>}
              {batches?.length === 0 && <p className="px-4 py-6 text-sm text-slate-500" data-testid="fee-payout-history-empty">No payouts run for {label} yet.</p>}
              {batches?.map(b => (
                <button key={b.id} onClick={() => setOpenBatch(b.id)} className="w-full grid grid-cols-12 gap-3 px-4 py-3 items-center hover:bg-slate-50 text-left" data-testid={`fee-payout-batch-${b.id}`}>
                  <div className="col-span-6 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{b.landlord_name}</p>
                    <p className="text-[11px] text-slate-400">{fmtDate(b.created_at)} · {b.count} payment{b.count === 1 ? '' : 's'}</p>
                  </div>
                  <div className="col-span-3 text-right text-sm tabular-nums text-slate-800">{money(b.amount)}</div>
                  <div className="col-span-3 flex justify-end"><Chip status={b.status} /></div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <LandlordSheet row={openRow} month={month} running={running} onRun={runPayout} onClose={() => setOpenRow(null)} />
      <BatchSheet batchId={openBatch} onClose={() => setOpenBatch(null)} />
    </div>
  );
}
